// AgentDrillDown — fetches per-agent breakdown (regions/districts/salons),
// renders via the shared report-shell <AgencyCard>.
//
// Owns:
//   - lazy fetch on first expand
//   - normalisation of operator-specific salon shape into { regions, flatSalons }
//   - per-operator column config
//
import React, { useEffect, useMemo, useState } from 'react'
import { apiService } from '../../services/api'
import { AgencyCard } from '../report-shell'

const AgentDrillDown = ({
  agent, operator, summary = {}, filters = {},
  onSendReport, onSendScope,
  accentColor,
}) => {
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const fetchData = async () => {
    setLoading(true); setError(null)
    try {
      let resp
      if (operator === 'plus') resp = await apiService.getPlusAgentDrillDown(filters)
      else if (operator === 'orange') resp = await apiService.getOrangeAgentDrillDown(filters)
      else if (operator === 'tmobile') resp = await apiService.getTmobileAgentDrillDown(filters)
      else throw new Error(`Unknown operator: ${operator}`)
      setData(resp.data)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    } finally { setLoading(false) }
  }

  useEffect(() => {
    if (expanded && !data && !loading) fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded])

  const columns = useMemo(() => columnsByOperator(operator), [operator])

  // Normalise raw drill-down rows into { regions[], flatSalons? } expected by AgencyCard.
  const { regions, flatSalons } = useMemo(() => {
    if (!data?.salons) return { regions: null, flatSalons: null }
    if (operator === 'tmobile') {
      // Flat list — TMPL has no region/district structure yet.
      const flat = data.salons.map((s, i) => ({ id: s.code || i, ...s }))
      return { regions: null, flatSalons: flat }
    }

    const regionField = operator === 'orange' ? 'nazwa_regionu' : 'region'
    const districtField = operator === 'orange' ? 'nazwa_dystryktu' : null

    const byRegion = {}
    for (const s of data.salons) {
      const region = (s[regionField] || s.region_bscs || '(brak regionu)').toString()
      if (!byRegion[region]) byRegion[region] = { name: region, salons: [], byDistrict: {} }
      const row = withRowId(s, operator)
      byRegion[region].salons.push(row)
      if (districtField) {
        const district = (s[districtField] || s.district_bscs || '(brak dystryktu)').toString()
        if (!byRegion[region].byDistrict[district]) {
          byRegion[region].byDistrict[district] = { name: district, salons: [] }
        }
        byRegion[region].byDistrict[district].salons.push(row)
      }
    }
    const stats = (arr) => ({
      salon_count: arr.length,
      sales_in_period: arr.reduce((a, x) => a + (Number(x.sales_in_period) || 0), 0),
      sales_current_month: arr.reduce((a, x) => a + (Number(x.sales_current_month) || 0), 0),
      sales_yesterday: arr.reduce((a, x) => a + (Number(x.sales_yesterday) || 0), 0),
    })
    const regs = Object.values(byRegion)
      .map(r => ({
        name: r.name,
        salons: r.salons,
        stats: stats(r.salons),
        districts: districtField
          ? Object.values(r.byDistrict)
              .map(d => ({ name: d.name, salons: d.salons, stats: stats(d.salons) }))
              .sort((a, b) => b.stats.sales_in_period - a.stats.sales_in_period)
          : null,
      }))
      .sort((a, b) => b.stats.sales_in_period - a.stats.sales_in_period)

    return { regions: regs, flatSalons: null }
  }, [data, operator])

  return (
    <AgencyCard
      agency={agent}
      summary={summary}
      tags={[]}
      accentColor={accentColor}
      expanded={expanded}
      onToggle={() => setExpanded(v => !v)}
      onSendReport={onSendReport ? () => onSendReport(agent) : undefined}
      onSendScope={onSendScope}
      loading={loading}
      error={error}
      regions={regions}
      flatSalons={flatSalons}
      columns={columns}
    />
  )
}

function withRowId(row, operator) {
  const id = operator === 'plus' ? row.sfid
            : operator === 'orange' ? (row.plant_code ?? row.kod_bscs)
            : row.code
  return { id: id ?? `${operator}-${Math.random()}`, ...row }
}

function columnsByOperator(operator) {
  if (operator === 'plus') {
    return [
      { field: 'sfid', header: 'SFID', width: '120px', numeric: true },
      { field: 'miasto', header: 'Miasto', width: '140px' },
      { field: 'pos_type', header: 'Typ', width: '80px' },
      { field: 'rks', header: 'RKS', width: '1.4fr' },
      { field: 'rks_phone', header: 'Tel RKS', width: '130px', phone: true },
      { field: 'sales_in_period', header: 'Sprzedaż okres', width: '120px', sales: true },
      { field: 'sales_current_month', header: 'Sprzedaż m-c', width: '110px', sales: true },
      { field: 'sales_yesterday', header: 'Wczoraj', width: '90px', sales: true },
    ]
  }
  if (operator === 'orange') {
    return [
      { field: 'kod_bscs', header: 'BSCS', width: '110px', numeric: true },
      { field: 'city', header: 'Miasto', width: '140px' },
      { field: 'kam', header: 'KAM', width: '110px' },
      { field: 'siec_sprzedazy', header: 'Sieć', width: '110px' },
      { field: 'rksa', header: 'RKSA', width: '1.3fr' },
      { field: 'xdm', header: 'XDM', width: '160px' },
      { field: 'xdm_phone', header: 'Tel XDM', width: '130px', phone: true },
      { field: 'sales_in_period', header: 'Sprzedaż okres', width: '120px', sales: true },
      { field: 'sales_current_month', header: 'MTD', width: '90px', sales: true },
      { field: 'sales_yesterday', header: 'Wczoraj', width: '90px', sales: true },
    ]
  }
  // tmobile
  return [
    { field: 'code', header: 'Kod POS', width: '130px', numeric: false },
    { field: 'city', header: 'Miasto', width: '140px' },
    { field: 'address', header: 'Adres', width: '1.4fr' },
    { field: 'agent_contact_phone', header: 'Tel agenta', width: '130px', phone: true },
    { field: 'category', header: 'Kategoria', width: '110px' },
    { field: 'sales_in_period', header: 'Sprzedaż okres', width: '120px', sales: true },
    { field: 'sales_current_month', header: 'MTD', width: '90px', sales: true },
    { field: 'sales_yesterday', header: 'Wczoraj', width: '90px', sales: true },
  ]
}

export default AgentDrillDown
